/* eslint-disable react-hooks/exhaustive-deps */
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useEffect, type Dispatch, type SetStateAction } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAccountConsent } from "@/api/account";
import { getUser } from "@/api/users";
import { cancelPayment, initPayment, patchPayment } from "@/api/payment";
import { CircleX, Wallet2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { addMinutes, fromUnixTime, getUnixTime } from "date-fns";
import { toast } from "sonner";
import {
  FULL_WIDTH_TALL_BUTTON,
  GREEN_ACTION_BUTTON_DISABLED,
  GREEN_ACTION_BUTTON_ENABLED,
  OUTLINE_PRESSABLE_BUTTON,
} from "@/lib/buttonStyles";

interface Props {
  status: string;
  expiresAt: number;
  paymentId: string;
  isLoading: boolean;
  setRefetchInterval: () => void;
  setIsLoading: (value: boolean) => void;
  openWallet: () => void;
  setAuthPhoneNumber: Dispatch<SetStateAction<string | undefined>>;
}

export default function InitPaymentUser({
  status,
  expiresAt,
  paymentId,
  isLoading,
  setRefetchInterval,
  setIsLoading,
  openWallet,
  setAuthPhoneNumber,
}: Props) {
  const schema = z.object({
    // Only show "too short" when user has entered at least 1 digit
    phoneNumber: z
      .string()
      .trim()
      .refine((val) => val.length === 0 || val.length === 7, {
        message: "Símanúmer of stutt",
      })
      .refine((val) => val.length === 0 || /^\d+$/.test(val), {
        message: "Símanúmer má aðeins innihalda tölustafi",
      })
      .refine((val) => val.length > 0, {
        message: "Sláðu inn símanúmer",
      }),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      phoneNumber: localStorage.getItem("phone") || "",
    },
  });

  const {
    data: user,
    isError: userNotFound,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ["user"],
    queryFn: () => getUser(`+354${form.getValues("phoneNumber")}`),
    enabled: false,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const { data: accountConsent } = useQuery({
    queryKey: ["account-consent", user?.id],
    queryFn: () => getAccountConsent(String(user?.id)),
    enabled: !!user?.id,
  });

  const initPaymentMutation = useMutation({
    mutationFn: async (variables: { phoneNumber?: string }) => {
      return initPayment(String(paymentId), variables.phoneNumber, "", "");
    },
    onSuccess: async () => {
      setRefetchInterval();
    },
    onError: (error: Response) => {
      // The handler is returning status 400 if the user is not found.
      // We should fix this and make it return 404 so we can handle multiple types of error
      if (error.status === 400) {
        patchAndRedirect(); // Redirect ther user to the onboarding page.
      }
      setIsLoading(false);
      toast.error("Ekki tókst að framkvæma greiðslu");
    },
  });

  const patchPaymentMutation = useMutation({
    mutationFn: async (variables: { phoneNumber?: string }) => {
      // Add 5 minutes to expiresAt
      const expiration = fromUnixTime(expiresAt);
      const extendedExpiration = addMinutes(expiration, 5);
      return patchPayment(
        String(paymentId),
        variables.phoneNumber,
        "",
        "",
        getUnixTime(extendedExpiration),
      );
    },
    onSuccess: async () => {
      setRefetchInterval();
      window.location.href = window.location.href.replace("payment", "light"); // redirect to the light page
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const cancelPaymentMutation = useMutation({
    mutationFn: async () => {
      return cancelPayment(String(paymentId));
    },
    onSuccess: () => {
      setRefetchInterval();
    },
    onError: () => {
      toast.error("Ekki tókst að hætta við greiðslu");
    },
  });

  const onSubmit = () => {
    setIsLoading(true);
    localStorage.setItem("phone", form.getValues("phoneNumber"));
    refetchUser();
  };

  const patchAndRedirect = async () => {
    patchPaymentMutation.mutate({
      phoneNumber: `+354${form.getValues("phoneNumber")}`,
    });
    // Redirect to the onboarding page
    window.location.href = window.location.href.replace("payment", "light");
  };

  // TODO: We will remove the redirect to the Onboarding page later
  useEffect(() => {
    // Redirect the user to the onboarding page if they are a shadow user.
    if (user && user.onboardedAt === 0) {
      const url = new URL(window.location.href.replace("payment", "light"));
      url.searchParams.set("id", paymentId);
      window.location.href = url.toString();
      return;
    }
    // If the user is onboarded we init the payment and polling
    if (user && user.onboardedAt > 0 && accountConsent) {
      // If we need the user to give us bank account consent again for isb
      if (!accountConsent.accountExists || accountConsent.consentExpired) {
        const url = new URL(window.location.href.replace("payment", "light"));
        if (accountConsent.consentExpired) {
          url.searchParams.append("reason", "consent");
        }

        // Redirect
        window.location.href = url.toString();
        return;
      }
      if (!accountConsent.consentExpired) {
        // If the consent is fine then we init the payment
        setIsLoading(true);
        initPaymentMutation.mutate({
          phoneNumber: user.phoneNumber,
        });
      }
    }
  }, [user, accountConsent]);

  useEffect(() => {
    //isError returns true if the user is not found. Redirect to the onboading page
    if (userNotFound) {
      // Patch the payment with the phone number
      patchPaymentMutation.mutate({
        phoneNumber: `+354${form.getValues("phoneNumber")}`,
      });
    }
  }, [userNotFound]);

  useEffect(() => {
    if (form.getValues("phoneNumber")) {
      setAuthPhoneNumber(form.getValues("phoneNumber"));
    }
  }, [form.getValues("phoneNumber")]);

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel>Símanúmer</FormLabel>
                <FormControl>
                  <Input
                    id="phoneNumber"
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value.replace(/\D/g, ""))
                    }
                    autoComplete="tel-local"
                    maxLength={7}
                    className="h-12"
                    inputMode="tel"
                    pattern="[0-9]*"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-col gap-4">
            <Button
              type="submit"
              className={
                form.formState.disabled || isLoading
                  ? `${FULL_WIDTH_TALL_BUTTON} ${GREEN_ACTION_BUTTON_DISABLED}`
                  : `${FULL_WIDTH_TALL_BUTTON} ${GREEN_ACTION_BUTTON_ENABLED}`
              }
              disabled={
                form.watch("phoneNumber").length !== 7 ||
                isLoading ||
                !["DRAFT", "PENDING", "SCA_REQUIRED"].includes(status)
              }
            >
              Greiða
              {isLoading && <Spinner className="size-4 absolute right-4" />}
            </Button>
            <Button
              type="button"
              variant={"outline"}
              className={`${FULL_WIDTH_TALL_BUTTON} ${OUTLINE_PRESSABLE_BUTTON}`}
              onClick={() => cancelPaymentMutation.mutate()}
              disabled={cancelPaymentMutation.isPending}
            >
              <div className="flex items-center gap-1">
                <span>Hætta við</span>
                {cancelPaymentMutation.isPending ? (
                  <Spinner className="size-4" />
                ) : (
                  <CircleX />
                )}
              </div>
            </Button>
            <Separator className="mt-4" />
            <p className="text-sm text-muted-foreground">
              Fyrir þá sem vilja skrá fleiri en einn bankareikning
            </p>
            <Button
              type="button"
              variant={"outline"}
              className={`${FULL_WIDTH_TALL_BUTTON} ${OUTLINE_PRESSABLE_BUTTON}`}
              onClick={openWallet}
            >
              <div className="flex items-center gap-1">
                <span>Veski</span>
                <Wallet2 />
              </div>
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
}
